// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import ListItem, { OrderedList, UnorderedList } from "@insite/mobius/Lists";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { DOMNode, domToReact, HTMLReactParserOptions } from "html-react-parser";
import * as React from "react";

interface ParserOptionsProps {
    onLinkClick?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
}

export const createParserOptions = ({ onLinkClick }: ParserOptionsProps = {}): HTMLReactParserOptions => {
    const parserOptions: HTMLReactParserOptions = {
        replace: node => {
            if (!("name" in node && "children" in node)) {
                return;
            }

            const { name, children, attribs = { style: "" } } = node;
            const { style, class: className, ...otherAttribs } = attribs;

            otherAttribs.className = className;

            // <XNG-Changes>
            if (name && ["html", "body"].includes(name) && children) {
                return <>{children && domToReact(children as DOMNode[], parserOptions)}</>;
            }
            if (name === "head") {
                return <></>;
            }
            // </XNG-Changes>

            if (name && ["h1", "h2", "h3", "h4", "h5", "h6", "body", "p", "legend"].includes(name) && children) {
                return (
                    <Typography css={style as any} {...otherAttribs} variant={name as TypographyProps["variant"]}>
                        {children && domToReact(children as DOMNode[], parserOptions)}
                    </Typography>
                );
            }
            if (name === "ul") {
                return (
                    <UnorderedList css={style as any} {...otherAttribs}>
                        {children && domToReact(children as DOMNode[], parserOptions)}
                    </UnorderedList>
                );
            }
            if (name === "ol") {
                return (
                    <OrderedList css={style as any} {...otherAttribs}>
                        {children && domToReact(children as DOMNode[], parserOptions)}
                    </OrderedList>
                );
            }
            if (name === "li") {
                return (
                    <ListItem css={style as any} {...otherAttribs}>
                        {children && domToReact(children as DOMNode[], parserOptions)}
                    </ListItem>
                );
            }
            if (name === "img") {
                const { alt, ...remainingAttribs } = otherAttribs;
                return <LazyImage as="span" altText={alt} css={style as any} {...remainingAttribs} />;
            }
            if (name === "a") {
                // <XNG-Changes>
                let onClickHandler: ((event: React.MouseEvent<HTMLElement, MouseEvent>) => void) | undefined;
                for (const key in otherAttribs) {
                    if (key === "ng-click") {
                        onClickHandler = onLinkClick;
                    }
                }
                // </XNG-Changes>

                return (
                    <Link
                        css={style as any}
                        id={otherAttribs.name}
                        {...otherAttribs}
                        onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) => {
                            onClickHandler && onClickHandler(event);
                        }}
                    >
                        {children && domToReact(children as DOMNode[], parserOptions)}
                    </Link>
                );
            }
        },
    };

    return parserOptions;
};

const xParserOptions = createParserOptions();
export default xParserOptions;
